import { formatMoney, formatPercent, Loan } from '@nemuru-eng/js-api-client';
import { Insurance } from '@nemuru-eng/js-api-client/slim';

import { getInsuranceCalculator } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ConditionsAndProgress/Conditions/components/InsurancePopover';
import { SetupFeeEntity } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/entities/SetupFeeEntity';
import { LoanInsuranceMap } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/maps/loanInsurance.map';

export class LoanConditionsEntity {
  constructor(private readonly loan: Loan) {}

  private get principalAmount() {
    return this.loan.loanConditions.principal.amount;
  }

  public get currency() {
    return this.loan.loanConditions.currency;
  }

  private get setupFeeAmount() {
    return this.loan.loanConditions.loanCalculator.setupFee.feeAmount?.amount;
  }

  public get principal() {
    return formatMoney({
      value: this.principalAmount,
      currency: this.currency,
    });
  }

  public get principalMoney() {
    return {
      value: this.principalAmount,
      currency: this.currency,
    };
  }

  public get term() {
    return this.loan.loanConditions.loanCalculator.term.period;
  }

  public get totalDownPayment() {
    const totalDownPayment = this.loan.assetDetails.totalBudget.amount - this.principalAmount;
    return totalDownPayment <= 0 ? null : formatMoney({ value: totalDownPayment, currency: this.currency });
  }

  public get program() {
    return this.program;
  }

  public get merchantDiscount() {
    return this.loan.loanConditions.program.merchantDiscountPercentage;
  }

  public get userDefinedQuota() {
    return this.loan.loanConditions.loanCalculator.userDefinedQuota
      ? formatMoney({
          value: this.loan.loanConditions.loanCalculator.userDefinedQuota.amount,
          currency: this.currency,
        })
      : null;
  }

  public get totalAmountPaid() {
    return this.loan.loanConditions.loanCalculator.totalAmountPaid
      ? formatMoney({
          value: this.loan.loanConditions.loanCalculator.totalAmountPaid.amount,
          currency: this.currency,
        })
      : null;
  }

  public get totalInterestPaid() {
    const totalInterestPaid = this.loan.loanConditions.loanCalculator.totalInterestPaid;
    return totalInterestPaid ? formatMoney({ value: totalInterestPaid.amount, currency: this.currency }) : null;
  }

  public get totalAssetAmount() {
    const totalAssetAmount = this.loan.assetDetails.totalBudget;
    return formatMoney({ value: totalAssetAmount.amount, currency: this.currency });
  }

  public get totalMerchantDiscount() {
    return formatMoney({
      value: this.principalAmount * (this.merchantDiscount ?? 0),
      currency: this.currency,
    });
  }

  public get totalAmountToCollect() {
    if (!this.merchantDiscount) return null;
    const totalAmountToCollectValue = this.merchantDiscount
      ? this.principalAmount - this.principalAmount * this.merchantDiscount
      : this.principalAmount;
    return formatMoney({ value: totalAmountToCollectValue, currency: this.currency });
  }

  public get annualInterest() {
    return this.loan.loanConditions.annualInterest;
  }

  public get tae() {
    return this.loan.loanConditions.loanCalculator.tae;
  }

  public get setupFee() {
    return SetupFeeEntity.create({
      term: this.term ?? undefined,
      currency: this.currency,
      setupFeeAmount: this.setupFeeAmount,
      setupFeeType: this.setupFeeType ?? undefined,
      amount: this.principalAmount,
    });
  }

  public get gracePeriod() {
    return this.loan.loanConditions.loanCalculator.gracePeriod;
  }

  public get merchantDiscountPercentage() {
    return this.merchantDiscount ? formatPercent(this.merchantDiscount) : null;
  }

  public get setupFeeType() {
    return this.loan.loanConditions.loanCalculator.setupFee.type;
  }

  public get pricingInsurance() {
    return this.loan.pricingInsurance ?? null;
  }

  public get displayInsurance() {
    return LoanInsuranceMap.fromLoan(this.loan);
  }

  public get insuranceCalculator() {
    const insurance = this.loan.pricingInsurance as Insurance | null;

    if (!insurance) return null;

    return getInsuranceCalculator(insurance, this.principalMoney, this.term ?? 0);
  }
}
